/**
 * product-gallery-zoom.js — PDP full-screen media lightbox.
 *
 * <product-gallery-zoom> is rendered once per PDP from the end of
 * snippets/product-media-gallery.liquid. See that snippet's header for why it sits there
 * and why its media list arrives as a parameter rather than being resolved locally.
 *
 * TRIGGER CONTRACT
 *   Any element with data-zoom-open="{index}" opens the overlay at that media index.
 *   Listened for by DELEGATED click, so triggers can be added, re-rendered or swapped
 *   without re-binding anything here.
 *
 * DUPLICATE TRIGGERS
 *   The inline gallery renders BOTH a mobile swiper and a desktop grid over the same list,
 *   and CSS hides one per breakpoint (theme.css:1545, :1566). So every index exists twice in
 *   the DOM. Harmless for delegation, but focus return has to resolve the VISIBLE one.
 *
 * CLOSING
 *   Close button and Escape only. Not backdrop click — this is a full-screen view, not a
 *   modal, and the backdrop is part of the view.
 *
 * Built to docs/Features/PDP/pdp-zoom-gallery-plan.md — lifecycle, main strip, thumbnail
 * rail, zoom and the GA4 open event.
 */
(function () {
  'use strict';

  if (customElements.get('product-gallery-zoom')) return;

  const FOCUSABLE = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';
  const SCROLL_LOCK_CLASS = 'pgz-open';

  /* 64rem at the theme's 16px root (theme.css:89). Swiper measures the window in px, so this
     must stay in step with BOTH the media query in the snippet's stylesheet and the media
     condition in its `sizes` attribute — three places, one number.

     1024 rather than the inline gallery's 768: at tablet widths the desktop chrome collides
     with itself. The rail is centred at 444px wide and the controls sit bottom-right at
     223px, so on a 768px viewport the rail runs to x=606 while the controls start at x=525.
     This is the zoom viewer's own breakpoint and is deliberately NOT the inline gallery's —
     theme.css still swaps that one at 48rem. */
  const DESKTOP_BREAKPOINT = 1024;

  /* Figma desktop frame 1:10: 16px between slides, 40px before the first and after the last. */
  const DESKTOP_GAP = 16;
  const DESKTOP_EDGE = 40;

  /* Swiper writes the rail's slide spacing as inline margin, so this — not the stylesheet's
     --pgz-thumb-gap — is what actually separates the thumbnails. The custom property only
     feeds the rail's max-width sum. Change one, change the other. */
  const THUMB_GAP = 4;

  /* Pinch ceiling AND the level a double-tap jumps to — Swiper has one dial for both. Also
     the multiplier the hi-res upgrade sizes for, so the two can never disagree. */
  const MAX_ZOOM = 3;

  /* How much of a slide must be on screen for its thumbnail to read as current. Desktop shows
     several images at once, so "current" is a range, not a single index. */
  const VISIBLE_THRESHOLD = 0.5;

  /* Light/dark. The stored choice, once made, outranks the OS setting for good. */
  const THEME_KEY = 'pgz-theme';

  /* Relative luminance above which an accent is treated as "too pale to sit on white".
     Chosen to clear the brand green (~0.72) and catch white (1.0) and its neighbours. */
  const ACCENT_LIGHT_THRESHOLD = 0.85;

  /* syncActive() runs on every translate, so a free-scroll drag across ten images would queue
     ten announcements. Coalesce them and speak only where the strip settles. */
  const ANNOUNCE_DELAY = 350;

  /* Swiper's own default. Dropped to 0 when the visitor asks for reduced motion — the CSS
     media query cannot reach Swiper's scripted transitions. */
  const SLIDE_SPEED = 400;

  /** #rgb, #rrggbb, rgb()/rgba() -> [r, g, b], or null if it is something else. */
  function parseColor(value) {
    const hex = value.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
    if (hex) {
      const h = hex[1];
      const full = h.length === 3 ? h.replace(/./g, (ch) => ch + ch) : h;
      return [0, 2, 4].map((i) => parseInt(full.slice(i, i + 2), 16));
    }

    const fn = value.match(/^rgba?\(([^)]+)\)$/i);
    if (fn) {
      const parts = fn[1].split(/[\s,\/]+/).filter(Boolean).slice(0, 3).map(Number);
      if (parts.length === 3 && parts.every((n) => Number.isFinite(n))) return parts;
    }

    return null;
  }

  /** WCAG relative luminance, 0 (black) to 1 (white). */
  function relativeLuminance([r, g, b]) {
    const channel = (v) => {
      const n = v / 255;
      return n <= 0.03928 ? n / 12.92 : ((n + 0.055) / 1.055) ** 2.4;
    };
    return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
  }

  /** Is the element actually rendered? offsetParent is null for display:none subtrees. */
  const isVisible = (el) => !!el && (el.offsetWidth > 0 || el.offsetHeight > 0 || el.getClientRects().length > 0);

  class ProductGalleryZoom extends HTMLElement {
    connectedCallback() {
      this.template = this.querySelector('[data-pgz-template]');
      this.populated = false;
      this.isOpen = false;
      this.lastFocused = null;
      /* The marked image: what the rail shows, what the arrows step from, and where focus
         returns on close. Also the diff guard in syncActive(), so it starts null — 0 would
         make the first readout on image 0 look like a no-op and mark nothing. */
      this.currentIndex = null;
      /* The index the shopper last explicitly asked for — a thumbnail, an arrow, a key, or
         the image they opened on. Cleared the moment they drag the strip themselves. */
      this.pinnedIndex = null;
      this.mainSwiper = null;
      this.railSwiper = null;
      this.desktop = window.matchMedia(`(min-width: ${DESKTOP_BREAKPOINT}px)`);
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.announceTimer = 0;
      /* Exactly the elements this component made inert, so close restores those and nothing
         else — anything already inert for its own reasons must stay that way. */
      this.inerted = [];

      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onDocumentKeydown = this.onDocumentKeydown.bind(this);
      this.onOverlayClick = this.onOverlayClick.bind(this);
      this.onGestureStart = this.onGestureStart.bind(this);
      this.syncZoomAvailability = this.syncZoomAvailability.bind(this);
      this.onMediaLoad = this.onMediaLoad.bind(this);
      this.syncMotion = this.syncMotion.bind(this);

      document.addEventListener('click', this.onDocumentClick);
      this.addEventListener('click', this.onOverlayClick);
      this.addEventListener('gesturestart', this.onGestureStart);
      this.desktop.addEventListener('change', this.syncZoomAvailability);
      this.reducedMotion.addEventListener('change', this.syncMotion);

      /* load and error do not bubble, so capture. Bound to the host rather than to each image
         so it covers the whole template the moment it is cloned in. */
      this.addEventListener('load', this.onMediaLoad, true);
      this.addEventListener('error', this.onMediaLoad, true);

      /* Both run before the overlay is ever shown — the host element is in the page from the
         start, only its contents are deferred — so there is no flash of the wrong mode and no
         flash of accent-coloured chrome. */
      this.applyTheme(this.readTheme());
      this.flagAccent();
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick);
      document.removeEventListener('keydown', this.onDocumentKeydown);
      this.desktop.removeEventListener('change', this.syncZoomAvailability);
      this.reducedMotion.removeEventListener('change', this.syncMotion);
      this.setBackgroundInert(false);
      clearTimeout(this.announceTimer);
      this.mainSwiper?.destroy(true, true);
      this.railSwiper?.destroy(true, true);
      this.mainSwiper = null;
      this.railSwiper = null;
      this.unlockScroll();
    }

    /* ---------------------------------------------------------------- triggers */

    onDocumentClick(event) {
      const trigger = event.target.closest?.('[data-zoom-open]');
      if (!trigger) return;

      event.preventDefault();
      const index = parseInt(trigger.getAttribute('data-zoom-open'), 10);
      this.open(Number.isNaN(index) ? 0 : index);
    }

    onOverlayClick(event) {
      if (event.target.closest('[data-pgz-close]')) {
        this.close();
        return;
      }

      /* Arrows step the readout by one, which is also what moves the strip. At the end of a
         short strip the images are all on screen already, so the highlight walks on while the
         strip stays put — that is the honest result of "next image", not a dead button.

         aria-disabled rather than the disabled attribute: a disabled button drops out of the
         tab order, so holding Next to the end would throw focus to the body. */
      if (event.target.closest('[data-pgz-theme-toggle]')) {
        this.toggleTheme();
        return;
      }

      const arrow = event.target.closest('[data-pgz-next], [data-pgz-prev]');
      if (arrow) {
        if (arrow.getAttribute('aria-disabled') === 'true') return;
        const step = arrow.hasAttribute('data-pgz-next') ? 1 : -1;
        this.goTo((this.currentIndex ?? 0) + step);
        return;
      }

      /* Thumbnail activation.
         Both routes have to set the pin, but only one of them has to move the strip:

           pointer — Swiper's Thumbs module already moved it. It listens for its own 'tap'
                     event, synthesised from the pointer sequence (thumbs.mjs, onThumbClick),
                     and calls slideTo internally. Calling goTo() here as well would restart
                     the same transition mid-flight. So: pin and re-read, nothing more.
           keyboard — Enter or Space on a focused <button> dispatches a click with no pointer
                     sequence, so Swiper's 'tap' never fires and nothing has moved yet.

         event.detail is 0 for exactly the keyboard case and non-zero for real pointer clicks.

         This split is the fix for the original defect: the pin used to be set only in goTo(),
         which a mouse click never reached, so clicking a thumbnail moved the images while the
         rail went on reporting the previous one. */
      const thumb = event.target.closest('[data-pgz-thumb]');
      if (!thumb) return;

      const index = this.clampIndex(parseInt(thumb.getAttribute('data-pgz-index'), 10) || 0);
      if (event.detail === 0) {
        this.goTo(index);
      } else {
        this.pinnedIndex = index;
        this.syncActive();
      }
    }

    onDocumentKeydown(event) {
      if (!this.isOpen) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }
      if (event.key === 'Tab') {
        this.trapFocus(event);
        return;
      }

      const last = this.thumbs.length - 1;
      let target = null;
      const from = this.currentIndex ?? 0;
      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') target = from + 1;
      else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') target = from - 1;
      else if (event.key === 'Home') target = 0;
      else if (event.key === 'End') target = last;

      if (target !== null) {
        event.preventDefault();
        this.goTo(target);
      }
    }

    /* --------------------------------------------------------------- lifecycle */

    /**
     * Clone the inert <template> in. Idempotent, and deferred to the first open, so no
     * lightbox image is fetched for a shopper who never opens it.
     */
    populate() {
      if (this.populated || !this.template?.content) return;

      this.appendChild(this.template.content.cloneNode(true));
      this.populated = true;

      this.main = this.querySelector('[data-pgz-main]');
      this.rail = this.querySelector('[data-pgz-rail]');
      this.closeButton = this.querySelector('[data-pgz-close]');
      this.prevButton = this.querySelector('[data-pgz-prev]');
      this.nextButton = this.querySelector('[data-pgz-next]');
      this.band = this.querySelector('[data-pgz-band]');
      this.status = this.querySelector('[data-pgz-status]');
      this.slides = Array.from(this.querySelectorAll('[data-pgz-index]:not([data-pgz-thumb])'));
      this.thumbs = Array.from(this.querySelectorAll('[data-pgz-thumb]'));

      this.initSwipers();

      // The toggle only exists once the template is cloned, so its label is set here.
      this.applyTheme(this.theme);
      this.sweepLoadedImages();
    }

    /**
     * One Swiper over the strip.
     *
     * slidesPerView is 'auto' at BOTH breakpoints on purpose, so CSS stays the single source
     * of truth for slide width — 100% on phone, auto (ratio-derived) on desktop. Handing
     * Swiper a numeric slidesPerView would make it write inline widths that fight the
     * fit-by-height rule.
     *
     * Widths resolve before the images decode: image_tag emits intrinsic width and height
     * attributes, so the browser knows each ratio up front and `width: auto` against a pinned
     * height gives the right number on the first layout pass.
     *
     * The instance is built while the overlay is still hidden. That is safe because .pgz is
     * `visibility: hidden`, not `display: none` — it is laid out, so Swiper can measure it.
     */
    initSwipers() {
      if (this.mainSwiper || typeof Swiper !== 'function' || !this.main) return;

      /* The rail is built FIRST: the main strip takes it as a constructor parameter, and the
         Thumbs module resolves that reference once, at init.

         watchSlidesProgress is load-bearing, not an optimisation — Thumbs.update() reads
         thumbsSwiper.visibleSlidesIndexes to decide whether the active thumbnail has scrolled
         out of sight, and that array is only populated when this is on. Without it the rail
         silently stops following past the eighth image.

         The rail sizes itself: CSS gives it width max-content capped at eight thumbs, so a
         product with fewer stays centred at its natural width and a longer one scrolls. */
      const reduced = this.reducedMotion.matches;

      if (this.rail) {
        this.railSwiper = new Swiper(this.rail, {
          slidesPerView: 'auto',
          spaceBetween: THUMB_GAP,
          watchSlidesProgress: true,
          grabCursor: true,
          speed: reduced ? 0 : SLIDE_SPEED,
          freeMode: { enabled: true, momentum: !reduced },
        });
      }

      this.mainSwiper = new Swiper(this.main, {
        slidesPerView: 'auto',
        spaceBetween: 0,
        grabCursor: true,
        watchSlidesProgress: true,
        speed: reduced ? 0 : SLIDE_SPEED,
        // Phone: snap, one image per viewport width.
        freeMode: { enabled: false },
        /* Swiper's Navigation and Keyboard modules are deliberately NOT used. Both call
           slideNext/slidePrev straight on the instance, which would move the strip without
           going through goTo() — and goTo() is what pins the index the rail reports. Arrow
           buttons and arrow keys are wired by hand instead, so every way of navigating takes
           the same path and the readout can never disagree with what was asked for. */
        // Two-way link: a tap on a thumbnail moves the strip, and the strip moving scrolls the
        // rail to keep the active thumbnail visible.
        thumbs: { swiper: this.railSwiper },
        /* Double-tap and pinch. Swiper binds the toggle to its own 'doubleTap' — two taps
           inside 300ms — so a single tap does nothing, which is the behaviour asked for.
           Constructed enabled, then gated to phone by syncZoomAvailability(): `zoom` is not a
           breakpoint-safe parameter, because the module attaches its listeners once at init. */
        zoom: { enabled: true, maxRatio: MAX_ZOOM },
        breakpoints: {
          // Desktop: free scroll with the strip bleeding off the right edge.
          [DESKTOP_BREAKPOINT]: {
            spaceBetween: DESKTOP_GAP,
            slidesOffsetBefore: DESKTOP_EDGE,
            slidesOffsetAfter: DESKTOP_EDGE,
            freeMode: { enabled: true, momentum: !reduced, sticky: false },
          },
        },
        on: {
          /* setTranslate covers free-scroll drag and momentum, where activeIndex may not
             change at all; the others catch programmatic moves and relayouts. syncActive()
             diffs before it writes, so the frequency costs nothing. */
          setTranslate: () => this.syncActive(),
          slideChange: () => this.syncActive(),
          transitionEnd: () => this.syncActive(),
          /* Clearing the guard forces a full rewrite. The range can be identical across a
             resize while the band's pixel span is not — thumbnails are 39px on phone and 52px
             on desktop — so a diffed no-op would strand the band at the old width. */
          resize: () => {
            this.currentIndex = null;
            this.syncActive();
          },
          /* A real drag, not a tap — Swiper only raises this past its movement threshold.
             From here the shopper is driving position directly, so the last thing they asked
             for stops being what the rail should report. */
          sliderFirstMove: () => {
            this.pinnedIndex = null;
            this.syncActive();
          },
          /* A breakpoint change restores Swiper's originalParams wholesale, undoing any
             runtime motion settings — so reapply them after it fires. */
          breakpoint: () => this.syncMotion(),
          // Swiper hands the image element straight to this, so there is nothing to look up.
          zoomChange: (swiper, scale, imageEl) => this.upgradeZoomImage(imageEl, scale),
        },
      });

      this.syncZoomAvailability();
    }

    /* ------------------------------------------------------------- light / dark */

    /**
     * An explicit choice, once made, outranks the OS setting permanently — that is the point
     * of storing it. With nothing stored we follow prefers-color-scheme, which reports no
     * preference as "not dark", so the fallback is light either way.
     *
     * localStorage throws outright in some privacy modes rather than returning null, so every
     * access is guarded; failing to read a preference is never a reason to fail to open.
     */
    readTheme() {
      try {
        const stored = localStorage.getItem(THEME_KEY);
        if (stored === 'light' || stored === 'dark') return stored;
      } catch (error) {
        /* storage unavailable — fall through to the OS setting */
      }

      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }

    applyTheme(theme) {
      this.theme = theme;
      this.setAttribute('data-pgz-theme', theme);

      // The button offers the OTHER mode, so its label is the opposite of the current one.
      const toggle = this.querySelector('[data-pgz-theme-toggle]');
      toggle?.setAttribute(
        'aria-label',
        theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'
      );
    }

    toggleTheme() {
      const next = this.theme === 'dark' ? 'light' : 'dark';
      this.applyTheme(next);

      try {
        localStorage.setItem(THEME_KEY, next);
      } catch (error) {
        /* storage unavailable — the choice still applies for this view */
      }
    }

    /**
     * Flag a near-white accent so light mode can swap the chrome to black.
     *
     * Measured rather than assumed: the accent comes from a per-product metafield, so its
     * value is not known at build time. Read once — it cannot change while the page is open.
     * Anything unparseable leaves the attribute unset, which keeps the accent as-is; failing
     * open is the right way round, since the fallback is what the design specifies.
     */
    flagAccent() {
      const raw = getComputedStyle(this).getPropertyValue('--pgz-accent').trim();
      const rgb = parseColor(raw);
      if (!rgb) return;

      const isLight = relativeLuminance(rgb) > ACCENT_LIGHT_THRESHOLD;
      this.setAttribute('data-pgz-accent', isLight ? 'light' : 'dark');
    }

    /* -------------------------------------------------------------- skeleton */

    /**
     * Clear the skeleton once an image is in. Also on `error`: a broken image should show the
     * backdrop and its alt text, not sit on a grey panel for ever.
     */
    onMediaLoad(event) {
      const img = event.target;
      if (img instanceof HTMLImageElement && img.classList.contains('pgz__image')) {
        img.classList.add('is-loaded');
      }
    }

    /** A cached image can finish before the capture listener sees anything. */
    sweepLoadedImages() {
      this.querySelectorAll('.pgz__image').forEach((img) => {
        if (img.complete && img.naturalWidth > 0) img.classList.add('is-loaded');
      });
    }

    /* -------------------------------------------------------------------- zoom */

    /**
     * Zoom is phone-only. Desktop already shows the image fitted to a 862px-tall slide and its
     * pointer interaction is the free-scroll drag, so a double-click that zoomed instead would
     * fight it.
     *
     * Toggled rather than configured per breakpoint: the module attaches its pointer listeners
     * once, during init, so a `zoom` key inside `breakpoints` would be read but never acted on.
     */
    syncZoomAvailability() {
      const zoom = this.mainSwiper?.zoom;
      if (!zoom) return;

      if (this.desktop.matches) {
        if (zoom.scale > 1) zoom.out();
        zoom.disable();
      } else {
        zoom.enable();
      }
    }

    /**
     * iOS Safari raises `gesturestart` for a two-finger pinch and will zoom the PAGE unless it
     * is prevented. touch-action covers the other engines; this covers Safari. Scoped to the
     * open overlay on phone so a pinch anywhere else on the site behaves normally.
     */
    onGestureStart(event) {
      if (this.isOpen && !this.desktop.matches) event.preventDefault();
    }

    /**
     * Keep a zoomed image sharp.
     *
     * Swiper's zoom is a CSS transform: it magnifies whichever srcset candidate the browser
     * already picked for the FIT size, so at 3x on a high-DPR phone the image visibly softens.
     * Decode a larger candidate off-DOM first, then widen the live image's `sizes` so it
     * re-selects the now-cached file — same box, so no reflow and no visible pop.
     *
     * Once per image. The flag also stops a pinch from queueing a decode on every frame.
     *
     * This is the one piece carried over from the Sundae School theme's
     * product-gallery-zoom.js (tzUpgradeHiRes, :726); everything else here is Swiper's.
     */
    upgradeZoomImage(imageEl, scale) {
      if (!imageEl || scale <= 1) return;
      if (imageEl.dataset.pgzHires || !imageEl.srcset) return;

      // offsetWidth is the LAYOUT width, so it is unaffected by the transform already applied.
      const fitWidth = imageEl.offsetWidth;
      if (!fitWidth) return;

      const dpr = window.devicePixelRatio || 1;
      const needed = `${Math.round(fitWidth * MAX_ZOOM * dpr)}px`;

      const probe = new Image();
      probe.decoding = 'async';
      probe.sizes = needed;
      probe.srcset = imageEl.srcset;
      if (imageEl.src) probe.src = imageEl.src;

      const apply = () => {
        imageEl.sizes = needed;
        imageEl.dataset.pgzHires = '1';
      };

      if (probe.decode) probe.decode().then(apply).catch(() => {});
      else probe.addEventListener('load', apply);
    }

    open(index) {
      if (this.isOpen) return;

      this.lastFocused = document.activeElement;
      this.populate();
      if (!this.main) return;

      this.isOpen = true;
      const start = this.clampIndex(index);
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      this.lockScroll();
      this.setBackgroundInert(true);

      document.addEventListener('keydown', this.onDocumentKeydown);
      this.closeButton?.focus({ preventScroll: true });

      /* GA4. Fires on every open rather than once per page view, so both readings stay
         available: users-with-the-event gives an open rate, event count gives depth.

         `index` is the media position opened from; it needs registering as a custom dimension
         in GA4 before it is reportable, though the event records without that.

         Kept after the PDP media A/B test ended — zoom opens turned out to be the clearest
         behavioural read on the gallery, so this stays as an ongoing metric. Guarded because
         Consent Mode or a blocker can leave gtag undefined. */
      if (typeof gtag === 'function') {
        gtag('event', 'pdp_zoom_open', { index: start });
      }

      // A frame's grace before measuring: the overlay has only just been made visible, and on
      // a first open the cloned images are still resolving their boxes.
      requestAnimationFrame(() => {
        this.railSwiper?.update();
        this.mainSwiper?.update();
        this.goTo(start, 0);
      });
    }

    close() {
      if (!this.isOpen) return;

      this.isOpen = false;
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      this.unlockScroll();
      this.setBackgroundInert(false);
      document.removeEventListener('keydown', this.onDocumentKeydown);

      // Cleared so reopening on the same image announces again rather than staying silent.
      clearTimeout(this.announceTimer);
      if (this.status) this.status.textContent = '';
      // Reopening always starts at fit.
      if (this.mainSwiper?.zoom?.scale > 1) this.mainSwiper.zoom.out();

      this.restoreFocus();
    }

    /* ------------------------------------------------------------------- state */

    clampIndex(index) {
      const last = Math.max(0, this.thumbs.length - 1);
      return Math.min(Math.max(index, 0), last);
    }

    /** Move the strip. speed 0 is an instant jump, used when opening. */
    goTo(index, speed = 300) {
      const target = this.clampIndex(index);
      this.pinnedIndex = target;
      if (this.mainSwiper) this.mainSwiper.slideTo(target, this.reducedMotion.matches ? 0 : speed);
      // slideTo is a no-op when the strip is already there, and then no event fires.
      this.syncActive();
    }

    /**
     * The rail readout: one thumbnail, marked by the band.
     *
     * WHY THIS IS NOT SIMPLY activeIndex. Desktop runs slidesPerView: 'auto' with free
     * scroll, so two or three images share the viewport and no position-derived rule is right
     * on its own:
     *
     *   - activeIndex is the LEADING-edge slide, so the end of a four-image set reports 2
     *     while 2, 3 and 4 are on screen.
     *   - Most-centred inverts it: slideTo aligns to the leading edge and a short strip
     *     cannot scroll far anyway, so clicking thumbnail 4 would mark 3.
     *
     * SO INTENT WINS OVER GEOMETRY. A thumbnail, an arrow, a key or opening the gallery pins
     * that index and it stays marked, whatever the strip could actually scroll to. The pin is
     * dropped on sliderFirstMove — the moment the shopper drags the strip — and position
     * decides from then on.
     *
     * Every route to a new image must therefore set the pin. Swiper offers three that bypass
     * it, all handled: Navigation and Keyboard are not used at all (arrows and arrow keys are
     * wired by hand), and Thumbs' pointer tap is picked up in onOverlayClick.
     *
     * On phone one slide fills the viewport, so every branch returns the same index.
     */
    syncActive() {
      const swiper = this.mainSwiper;
      if (!swiper || !this.thumbs?.length) return;

      const index = this.pinnedIndex ?? this.positionalIndex(swiper);
      if (index === this.currentIndex) return;
      this.currentIndex = index;

      this.thumbs.forEach((thumb, i) => {
        if (i === index) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });

      this.prevButton?.setAttribute('aria-disabled', String(index <= 0));
      this.nextButton?.setAttribute('aria-disabled', String(index >= this.thumbs.length - 1));

      this.announce(index);

      this.positionBand(index, index);
    }

    /**
     * Which image the strip is showing, when the shopper is the one moving it.
     *
     * Anchored at the ends and centred in between, so a drag from one end to the other walks
     * the mark through every thumbnail in order rather than skipping or sticking:
     *   at the start -> first image on screen
     *   at the end   -> last image on screen
     *   in between   -> whichever image sits closest to the middle
     *
     * "On screen" means at least VISIBLE_THRESHOLD of the image — not Swiper's own visible
     * flag, which passes on a single pixel (core.mjs, updateSlidesProgress) and so counts the
     * sliver of the previous image at the left edge.
     *
     * Arithmetic over Swiper's measurements rather than getBoundingClientRect, because this
     * runs on every translate and reading layout there would thrash during momentum. The
     * left-edge term mirrors updateSlidesProgress, so it stays correct under
     * slidesOffsetBefore.
     */
    positionalIndex(swiper) {
      const viewport = swiper.size;
      const sizes = swiper.slidesSizesGrid || [];
      const centre = viewport / 2;

      const visible = [];
      let nearest = null;
      let nearestDistance = Infinity;

      swiper.slides.forEach((slide, i) => {
        const width = sizes[i];
        if (!width) return;

        const left = slide.swiperSlideOffset + swiper.translate;
        const covered = Math.min(left + width, viewport) - Math.max(left, 0);
        if (covered / width < VISIBLE_THRESHOLD) return;

        visible.push(i);
        const distance = Math.abs(left + width / 2 - centre);
        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearest = i;
        }
      });

      // A midway swipe can leave nothing past the threshold; never blank the band.
      if (!visible.length) return swiper.activeIndex;

      if (swiper.isBeginning) return visible[0];
      if (swiper.isEnd) return visible[visible.length - 1];
      return nearest;
    }

    /**
     * Stretch the band across thumbnails `first`..`last`.
     *
     * The band lives inside the rail's .swiper-wrapper, so it inherits the wrapper's transform
     * and tracks the thumbnails for free while the rail itself scrolls — JS only runs when the
     * range changes, not on every frame of a rail scroll. The stylesheet gives the wrapper
     * position: relative so both the band and the thumbnails' offsetLeft resolve against it.
     */
    positionBand(first, last) {
      const from = this.thumbs[first];
      const to = this.thumbs[last];
      if (!this.band || !from || !to) return;

      // First placement is a jump, not a slide: without this the band grows out of nothing
      // at the left edge every time the gallery opens.
      const isFirstPlacement = !this.band.style.width;
      if (isFirstPlacement) this.band.style.transition = 'none';

      const left = from.offsetLeft;
      this.band.style.transform = `translateX(${left}px)`;
      this.band.style.width = `${to.offsetLeft + to.offsetWidth - left}px`;

      if (isFirstPlacement) {
        requestAnimationFrame(() => {
          this.band.style.transition = '';
        });
      }
    }

    /* ------------------------------------------------------------------- focus */

    trapFocus(event) {
      const focusable = Array.from(this.querySelectorAll(FOCUSABLE)).filter(isVisible);
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;

      if (event.shiftKey && (active === first || !this.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    }

    /**
     * If the shopper browsed to a different image, the originally-clicked trigger is no
     * longer the logical anchor, so focus returns to the trigger for the image they ended
     * on. Both gallery loops carry that index, so pick the one actually rendered.
     */
    restoreFocus() {
      const index = this.currentIndex ?? 0;
      const candidates = Array.from(document.querySelectorAll(`[data-zoom-open="${index}"]`));
      const target = candidates.find(isVisible) || this.lastFocused;
      target?.focus?.({ preventScroll: true });
    }

    /* ---------------------------------------------------------------- motion */

    /**
     * Reduced motion beyond CSS. The stylesheet's media query cannot reach Swiper's scripted
     * transitions or its free-mode momentum, so both are set here and reapplied whenever a
     * breakpoint change restores Swiper's originalParams.
     */
    syncMotion() {
      const reduced = this.reducedMotion.matches;
      const speed = reduced ? 0 : SLIDE_SPEED;

      [this.mainSwiper, this.railSwiper].forEach((swiper) => {
        if (!swiper) return;
        swiper.params.speed = speed;
        if (swiper.params.freeMode) swiper.params.freeMode.momentum = !reduced;
      });
    }

    /* ------------------------------------------------------------ announcement */

    /**
     * Speak the position. Coalesced, because syncActive() runs on every translate and a
     * free-scroll drag would otherwise queue an announcement per image passed.
     */
    announce(index) {
      if (!this.status) return;

      clearTimeout(this.announceTimer);
      this.announceTimer = setTimeout(() => {
        this.status.textContent = `Image ${index + 1} of ${this.thumbs.length}`;
      }, ANNOUNCE_DELAY);
    }

    /* ------------------------------------------------------------ scroll lock */

    /**
     * Take the rest of the page out of the accessibility tree and the tab order.
     *
     * aria-modal="true" only ASKS assistive tech to ignore the background and is not
     * universally honoured, and the Tab trap is JS-only — a screen reader's virtual cursor
     * can still walk the page behind. `inert` is the real thing.
     *
     * Walks from this element up to <body>, inerting every sibling passed. Records exactly
     * what it changed so close restores those and nothing else: an element already inert for
     * its own reasons must stay inert afterwards.
     *
     * The walk stops below <html> — inerting <head> would be pointless.
     */
    setBackgroundInert(on) {
      if (!('inert' in HTMLElement.prototype)) return;

      if (!on) {
        this.inerted.forEach((el) => {
          el.inert = false;
        });
        this.inerted = [];
        return;
      }

      this.inerted = [];
      let node = this;
      while (node.parentElement && node.parentElement !== document.documentElement) {
        for (const sibling of node.parentElement.children) {
          if (sibling !== node && !sibling.inert) {
            sibling.inert = true;
            this.inerted.push(sibling);
          }
        }
        node = node.parentElement;
      }
    }


    lockScroll() {
      document.documentElement.classList.add(SCROLL_LOCK_CLASS);
    }

    unlockScroll() {
      document.documentElement.classList.remove(SCROLL_LOCK_CLASS);
    }
  }

  customElements.define('product-gallery-zoom', ProductGalleryZoom);
})();
